/**
 * Photography style analyzer — spawns a small internal agent that reads the
 * user's brief (and optionally reference image descriptions), writes a rich
 * Style DNA string, and pre-dials all camera controls in photography.md.
 *
 * Follows the same internal-agent pattern as `token-generator.ts`.
 */

import { z } from "zod";
import type { Logger } from "pino";

import type { AgentManager } from "../agent/agent-manager.js";
import {
  DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
  StructuredAgentFallbackError,
  StructuredAgentResponseError,
  generateStructuredAgentResponseWithFallback,
} from "../agent/agent-response-loop.js";
import type { BrandVariable } from "../brands/scanner.js";
import { listBrands, writeBrandFrontmatter } from "../brands/scanner.js";

export interface PhotographyAnalysisOptions {
  agentManager: AgentManager;
  workspaceRoot: string;
  brandPath: string;
  brief: string;
  referenceDescriptions?: string[];
  logger: Logger;
  /** auth-server user-id of the caller — stamped on the internal agent. */
  ownerUserId: string;
}

export interface PhotographyAnalysisResult {
  updatedCount: number;
}

const PhotographyResponseSchema = z.object({
  dna: z.string().min(1),
  dials: z.record(z.string(), z.string()),
});

/**
 * Build the prompt that tells the agent what to do with the brief, references,
 * and current dial state.
 */
function buildPrompt(args: {
  brief: string;
  referenceDescriptions: string[];
  selectVars: BrandVariable[];
}): string {
  const dialLines = args.selectVars
    .map((v) => {
      const options = (v.options ?? []).join(", ");
      return `- ${v.key} (${v.label || v.key}): current="${v.value || "(unset)"}" options=[${options}]`;
    })
    .join("\n");

  const refSection =
    args.referenceDescriptions.length > 0
      ? [
          "",
          "Reference image filenames/descriptions provided by the user:",
          ...args.referenceDescriptions.map((r) => `- ${r}`),
        ].join("\n")
      : "";

  return [
    "You are a photography director analyzing a brand's photographic identity.",
    "The user has provided a brief describing the visual direction they want.",
    "Your job is to:",
    "1. Write a rich, specific Style DNA (2-3 sentences describing the photographic",
    "   identity in concrete visual terms — lighting quality, tonal mood, depth",
    "   characteristics, color rendering. No marketing fluff.)",
    "2. Pre-dial every camera control to match that style direction.",
    "   ONLY use values that appear in the options list for each dial.",
    "",
    "User brief:",
    args.brief,
    refSection,
    "",
    "Camera dials (key, current value, valid options):",
    dialLines,
    "",
    "Return JSON only with the shape:",
    '{ "dna": "Rich style DNA description...", "dials": { "key": "value", ... } }',
    "",
    "Rules:",
    "- The dna field must be a non-empty string, 2-3 sentences.",
    "- Every value in dials must be one of the listed options for that key.",
    "- Include ALL dial keys in dials, even if you keep the current value.",
    "- Do not invent new keys. Do not output anything outside the JSON.",
  ]
    .filter((line) => line !== "")
    .join("\n");
}

export async function analyzeAndApplyPhotographyStyle(
  options: PhotographyAnalysisOptions,
): Promise<PhotographyAnalysisResult> {
  const {
    agentManager,
    workspaceRoot,
    brandPath,
    brief,
    referenceDescriptions,
    logger,
    ownerUserId,
  } = options;

  // Read the current prose/photography.md variables
  const brands = await listBrands({ workspaceRoot });
  const photoBrand = brands.find((b) => b.name === "photography") ?? null;
  if (!photoBrand) {
    throw new Error("Photography brand section not found — ensure prose/photography.md is present");
  }
  void brandPath; // API parameter preserved for backward compatibility

  const allVars = photoBrand.variables;
  const selectVars = allVars.filter(
    (v) => v.type === "select" && v.options && v.options.length > 0,
  );

  if (selectVars.length === 0) {
    throw new Error("No camera dials found in photography brand file");
  }

  // Build valid options lookup: key -> Set of valid option values
  const validOptions = new Map<string, Set<string>>();
  for (const v of selectVars) {
    validOptions.set(v.key, new Set(v.options ?? []));
  }

  // Spawn internal agent
  const agentPrompt = buildPrompt({
    brief,
    referenceDescriptions: referenceDescriptions ?? [],
    selectVars,
  });

  let response: { dna: string; dials: Record<string, string> };
  try {
    response = await generateStructuredAgentResponseWithFallback({
      manager: agentManager,
      cwd: workspaceRoot,
      prompt: agentPrompt,
      schema: PhotographyResponseSchema,
      schemaName: "PhotographyAnalysis",
      maxRetries: 2,
      providers: DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
      ownerUserId,
      agentConfigOverrides: {
        title: "Photography style analyzer",
        internal: true,
      },
    });
  } catch (error) {
    if (
      error instanceof StructuredAgentResponseError ||
      error instanceof StructuredAgentFallbackError
    ) {
      logger.warn({ err: error, brandPath }, "Structured photography analysis failed");
      throw new Error("Photography analysis failed — the agent did not return valid JSON");
    }
    throw error;
  }

  // Validate dial values — only accept values that are valid options
  const acceptedUpdates = new Map<string, string>();
  for (const [key, value] of Object.entries(response.dials)) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    const opts = validOptions.get(key);
    if (!opts) continue; // unknown key
    if (!opts.has(trimmed)) continue; // invalid option
    acceptedUpdates.set(key, trimmed);
  }

  // Always set dna and brief
  acceptedUpdates.set("photo.dna", response.dna);
  acceptedUpdates.set("photo.brief", brief);

  // Build next variables
  const nextVariables: BrandVariable[] = allVars.map((v) => {
    const update = acceptedUpdates.get(v.key);
    if (update === undefined) return v;
    return { ...v, value: update };
  });

  await writeBrandFrontmatter(
    {
      path: photoBrand.path,
      frontmatter: {
        description: photoBrand.description,
        variables: nextVariables,
      },
    },
    workspaceRoot,
  );

  logger.info(
    { brandPath, updatedCount: acceptedUpdates.size },
    "photography-analyzer: applied style analysis",
  );
  return { updatedCount: acceptedUpdates.size };
}
